import type { FlexProps } from "@qws-org/ui-kit";
import {
  Box,
  Flex,
  Grid,
  Image,
  Title,
  Typography,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, GridBlockData, Pages } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type GridBlockProps = {
  data: GridBlockData;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (action: Action) => boolean;
} & FlexProps;

export const GridBlock: FC<GridBlockProps> = ({
  data,
  getUrl,
  onCallAction,
  isControlInactive,
  ...rest
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();

  return (
    <Flex
      flexDirection="column"
      align="center"
      p={{ py: 40, px: 15 }}
      {...rest}
      d={{ p: { py: 80 }, ...rest.d }}
    >
      {data.title && (
        <Title
          as="h2"
          size="medium"
          fontWeight="desktop.semibold"
          horizontalAlign="center"
          m={{ mb: 24 }}
          d={{ m: { mb: 80 } }}
        >
          {data.title}
        </Title>
      )}

      {data.description && (
        <Typography
          size="large"
          fontWeight="desktop.semibold"
          horizontalAlign="center"
          lineHeight="body.mobile.large"
          md={{ lineHeight: "body.desktop.large" }}
          m={{ mb: 24 }}
        >
          {data.description}
        </Typography>
      )}

      <Grid
        gap={data.mobile_gap ?? 0}
        cols={data.mobile_columns ?? 1}
        md={{
          cols: data.tablet_columns,
          gap: data.tablet_gap ?? data.mobile_gap ?? 0,
        }}
        d={{
          cols: data.desktop_columns,
          gap: data.desktop_gap ?? data.tablet_gap ?? data.mobile_gap ?? 0,
        }}
        width="100%"
      >
        {data.items?.map((item) => {
          const controlAction = item?.grid_item_id?.action;

          const action = controlAction?.action?.[0];
          let href;
          if (isExternalLink(action?.item)) {
            href = action.item.href ?? undefined;
          }
          if (isInternalLink(action?.item)) {
            href = action.item.page ? getUrl(action.item.page) : undefined;
          }
          const handlePressButton = (): void => {
            if (controlAction) {
              onCallAction(controlAction);
            }
          };
          const ActionEl =
            controlAction && !isControlInactive(controlAction) ? Link : Box;
          return (
            <Grid.Item key={item?.id}>
              <ActionEl href={href} onClick={handlePressButton}>
                <Image
                  value={getDirectusImage(item?.grid_item_id?.image?.id ?? "", {
                    format: "webp",
                    height: data?.mobile_height ?? undefined,
                  })}
                  mdValue={getDirectusImage(
                    item?.grid_item_id?.image?.id ?? "",
                    {
                      format: "webp",
                      height: data?.tablet_height ?? undefined,
                    },
                  )}
                  dValue={getDirectusImage(
                    item?.grid_item_id?.image?.id ?? "",
                    {
                      format: "webp",
                      height: data?.desktop_height ?? undefined,
                    },
                  )}
                  display="block"
                  border={{ radius: "16px" }}
                  className="overflow-hidden"
                />
              </ActionEl>
            </Grid.Item>
          );
        })}
      </Grid>
    </Flex>
  );
};
